import { useFrame } from '@react-three/fiber';
import React, { FunctionComponent, memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';
import { Jute } from '../..';
import { transitionManager } from '../../hooks';
import { useAllSceneAutoplayVideoIds, useAllSceneEntityIds, useGet360VideoIdBySceneId, useModelIdleAnimationData, useSceneArEntityIds } from '../../hooks/general';
import { IEntityActionTypes, useActionStateStore } from '../../zustand';
import { useDataStore } from '../../zustand/data-store';
import { useSceneStore, ISceneActions, ITransCatTypes } from '../../zustand/scene-store';
import { IActionCategory, IComponentType, IPlaySoundAction, ISceneComp, ISpatialComponentUnion, ITransitionCategoryTypes, ITransitionData, ITransitionEffectTypes, ITuple3 } from '../r3f-components/component-data-structure';
import FadeGroup from '../FadeGroup';
import { useGetFaceTrackedComponentIds, useGetScreenRelativeComponentIds } from '../r3f-components/hooks/useGetScreenRelativeComponentIds';
import { getOpacityTransitionRange, getSpatialTransitionRange, IEasingTypes, Transition } from '../r3f-components/transitions';
import { ITransitionPhase } from '../r3f-components/transitions/spec';
import ArComponents from './ArComponents';
import { getAllOnloadModelAnimationPlayDataBySceneId } from '../r3f-components/utils/actions';
import { getAllAnimationControllerOptions } from '../../utils/action';
import { presetAnimationController } from '../PresetAnimationController';
import { return360VideoIdBySceneId } from '../r3f-components/utils/content360';
import { getAllModelIdsBySceneId } from '../r3f-components/utils/modelAnimations';

const transitionAddedDict: {
	[id: string]: boolean;
} = {};

interface IParentProps {
	id: string;
	visible?: boolean;
}

const Scene: FunctionComponent<IParentProps> = ({ id: sceneId, visible = true }) => {
	const fadeGroupRef = useRef<Group>(null);
	const previousSceneId = useSceneStore((state) => state.previousSceneId);
	// const { scene: threeScene } = useThree();
	
	// create transition objects on mount
	const isTestMode = Jute.environment === 'test';
	const sceneSpatialTransition = useMemo(
		() => new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `spatial-${sceneId}` }),
		[]
	);
	const sceneOpacityTransition = useMemo(() => new Transition<number>({ start: 0, end: 0, duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `opacity-${sceneId}` }), []);

	useFrame(({ camera }) => {
		camera.updateMatrixWorld();
		sceneSpatialTransition.tick();
		sceneOpacityTransition.tick();
	});

	const memoZeroTuple3 = useMemo(() => [0, 0, 0], []);

	// Data store never updates, so no need to pull in state transiently
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
	const scene = useMemo(() => componentsById?.[sceneId], [sceneId, componentsById]) as ISceneComp;
	const screenRelativeIds = useGetScreenRelativeComponentIds(sceneId, componentsById);
	const faceTrackedIds = useGetFaceTrackedComponentIds(sceneId, componentsById);
	const contentIds = useMemo(() => [...scene.children, ...screenRelativeIds, ...faceTrackedIds], [scene.children, screenRelativeIds, faceTrackedIds]);
	const [sceneDispatch] = useState(() => useSceneStore.getState().dispatch);
	const experienceIsMuted = useActionStateStore(({ experienceIsMuted }) => experienceIsMuted);
	const [entityActionDispatch] = useState(() => useActionStateStore.getState().dispatch);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);

	const sceneOnLoadPlaySoundActions = useMemo(() => scene?.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound), [scene]) as IPlaySoundAction[];

	// Re-rendering useful to intialise spring prop changes
	const transitionCategory = useSceneStore((state) => state.transitionCategory[sceneId]);

	const sceneModelIds = useSceneArEntityIds(sceneId, IComponentType.Model3d);
	const sceneVideoIds = useAllSceneEntityIds(sceneId, IComponentType.Video);
	const sceneModelIdleAnimationData = useModelIdleAnimationData(sceneId);
	const visibleAutoplayVideoIds = useAllSceneAutoplayVideoIds(sceneId, true);

	const scene360VideoId = useGet360VideoIdBySceneId(sceneId);

	if (scene360VideoId) visibleAutoplayVideoIds.push(scene360VideoId);

	useLayoutEffect(() => {
		if (!sceneId || !componentsById) return;
		entityActionDispatch?.({
			type: IEntityActionTypes.SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID,
			payload: { id: sceneId, componentsById },
		});
	}, [sceneId, componentsById, entityActionDispatch]);

	// REFACTOR: MOVE THIS TO A NEW HIGHER SCENE CONTENT component (parent of sceneSrContent and sceneArContent)

	// Play/pause media and animations depending on scene state changes.
	useLayoutEffect(() => {
		// Pause all video, animations and sound if in placement mode or scene hidden.
		if (placementMode || transitionCategory === 'hidden') {
			if (sceneModelIds?.length) {
				entityActionDispatch({
					type: IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID,
					payload: sceneModelIds,
				});
			}
			if (sceneVideoIds?.length && !!previousSceneId) {
				entityActionDispatch({
					type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
					payload: { ids: sceneVideoIds },
				});
			}
			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
				payload: { id: sceneId, resetToStart: true },
			});

			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
				payload: { ids: [return360VideoIdBySceneId(sceneId)] },
			})
			return;
		}
		if (transitionCategory === ITransCatTypes.enter) {
			if (visibleAutoplayVideoIds?.length) {
				entityActionDispatch({
					type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
					payload: { ids: visibleAutoplayVideoIds },
				});
			}
		}
	}, [transitionCategory, entityActionDispatch, visibleAutoplayVideoIds, placementMode, sceneModelIds, sceneVideoIds, sceneId]);

	const enterOrExit = useMemo(() => {
		return transitionCategory === ITransCatTypes.enter ? ITransitionCategoryTypes.enter : ITransitionCategoryTypes.exit;
	}, [transitionCategory]);

	useLayoutEffect(() => {
		if (!transitionAddedDict[sceneId]) {
			transitionManager[sceneId].add(sceneSpatialTransition);
			transitionAddedDict[sceneId] = true;
		}
	}, [sceneId, sceneSpatialTransition]);

	// set transition phases for scene depending on transition events (onFinish, onStart...) & categories
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;

		// console.log('set transition phase by category', sceneId, transitionCategory)
		transitionManager[sceneId].onStartAll(() => {
			// console.log('onStartAll in scene logic');
			// on scene enter start
			
			if (transitionCategory === ITransCatTypes.enter) {
				console.log('SCENE: start enter transition', sceneId);
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_PHASE,
					transPhaseDict: { [sceneId]: ITransitionPhase.enterStart },
				});
				
				presetAnimationController
					.set({options: getAllAnimationControllerOptions(componentsById!).preset})
					.startAllOnloadAnimationsBySceneId(sceneId)
				
				entityActionDispatch?.({
					type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
					payload: getAllOnloadModelAnimationPlayDataBySceneId(componentsById, sceneId)
				});
			
				sceneOnLoadPlaySoundActions?.forEach((action) => {
					let { stopMedia, loop } = action;
					if (stopMedia) {
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
							payload: { id: sceneId },
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
						});
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
						payload: { sceneId, entityIds: [sceneId] },
					});
				})
				return;
			}
			// on scene exit Start
			console.log('SCENE: start exit transition', sceneId);
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: { [sceneId]: ITransitionPhase.exitStart },
			});
		});
		transitionManager[sceneId].onFinishAll(() => {
			// console.log('onFinishAll in scene logic');
			// on scene enter finish
			if (transitionCategory === ITransCatTypes.enter) {
				console.log('SCENE: enter finish', sceneId);
				sceneDispatch?.({
					type: ISceneActions.SET_TRANSITION_PHASE,
					transPhaseDict: { [sceneId]: ITransitionPhase.enterFinish },
				});
				// Start play idle animations on scene transition enter finish.
				// if (sceneModelIdleAnimationData?.length) {
				// 	entityActionDispatch?.({
				// 		type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
				// 		payload: sceneModelIdleAnimationData,
				// 	});
				// }

				return;
			}
			console.log('SCENE: exit finish', sceneId);
			// on scene exit finish pause media
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: { [sceneId]: ITransCatTypes.hidden },
			});
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_PHASE,
				transPhaseDict: { [sceneId]: ITransitionPhase.exitFinish },
			});

			// Reset/pause all scene animations 
			presetAnimationController.resetAllAnimationsBySceneId(sceneId);
			if (!componentsById) return;
			entityActionDispatch?.({
				type: IEntityActionTypes.PAUSE_MODEL_ANIMATIONS_BY_ID,
				payload: getAllModelIdsBySceneId(sceneId, componentsById)
			})
		});
	}, [transitionCategory, sceneOnLoadPlaySoundActions, sceneDispatch, sceneId, entityActionDispatch, sceneVideoIds, sceneModelIdleAnimationData]);

	// Setting scene opacity transition for fade group
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;
		const { transitions } = scene;
		if (!transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit) return;
		let { type: effect, delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		// extend the scene fade transition duration to that of the longest child transition

		// TODO: check if correct
		if (transitions?.[enterOrExit]) {
			for (let i = 0; i < contentIds.length; i++) {
				const childId = contentIds[i];
				const transitionData = (componentsById?.[childId] as ISpatialComponentUnion).transitions?.[enterOrExit];
				if (!transitionData) continue;

				if (duration + delay < transitionData.duration + transitionData.delay) {
					// Extend duration to fit childs transition duration + delay
					duration = transitionData.duration + transitionData.delay - delay;
				}
			}
		}

		let { to, from } = getOpacityTransitionRange(transitionCategory);

		//
		if (duration === 0) {
			from = 1;
			to = 1;
		}

		// when entering, don't set scene transition to 0 when starting transiton with delay
		// to avoid unnecessary flashing.
		if (enterOrExit === ITransitionCategoryTypes.enter && duration !== 0 && effect !== ITransitionEffectTypes.fadeIn) {
			duration = duration + delay;
			delay = 0;
		}

		sceneOpacityTransition.set({ from, to, duration, delay });
		sceneOpacityTransition.start();
	}, [enterOrExit, sceneOpacityTransition, transitionCategory, scene, sceneId, componentsById, contentIds]);

	// Setting scene spatial transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden) return;
		// set up transition for new run
		const fadeGroup = fadeGroupRef.current;
		const { transitions } = scene;
		const effectFallback = enterOrExit === ITransitionCategoryTypes.exit ? ITransitionEffectTypes.fadeOut : ITransitionEffectTypes.fadeIn;
		let { type: effect = effectFallback, delay = 0, duration } = (transitions?.[enterOrExit] as ITransitionData) || {};
		const originalDuration = duration;
		duration = duration || 100; // 100ms required to ensure that pointer events does not affect children of both scenes

		const { x, y, z } = fadeGroupRef.current?.position as Vector3;
		const [sx, sy, sz] = transitionCategory === ITransCatTypes.exit ? [x, y, z] : [0, 0, 0];

		let { to, from, threeProperty } = getSpatialTransitionRange({
			startPosition: [sx, sy, sz],
			enterEndPosition: [0, 0, 0],
			effect,
			category: transitionCategory,
			positionOffset: 2,
		});

		if (!transitions?.[enterOrExit]?.duration) {
			switch (threeProperty) {
				case 'position':
					to = [0, 0, 0];
					from = [0, 0, 0];
					break;
				case 'scale':
					to = [1, 1, 1];
					from = [1, 1, 1];
			}
		}

		// TODO: Add opacity transition to transition manager to make sure opacity has finished before transiion manager
		// calls onfinish all and hides scene. For now the spatial transition is extended as a quick fix.
		if (effect === ITransitionEffectTypes.fadeOut) {
			for (let i = 0; i < contentIds.length; i++) {
				const childId = contentIds[i];
				const transitionData = (componentsById?.[childId] as ISpatialComponentUnion).transitions?.[enterOrExit];
				if (!transitionData) continue;
				if (duration + delay < transitionData.duration + transitionData.delay) {
					// Extend duration to fit childs transition duration + delay
					duration = transitionData.duration + transitionData.delay - delay;
				}
			}
		}

		// if the scene had a position or scale changing transition on exit and no position or scale changing
		// transition respectively on enter reset position/scale to the right values before
		if (transitionCategory === ITransCatTypes.enter) {
			switch (threeProperty) {
				case 'scale':
					fadeGroupRef.current?.position?.set?.(0, 0, 0);
					break;
				default:
					fadeGroupRef.current?.scale?.set?.(1, 1, 1);
					fadeGroupRef.current?.position?.set?.(0, 0, 0);
					break;
			}
		}

		sceneSpatialTransition.set({ from, to, duration, delay, easing: IEasingTypes.linear });

		// mutate threejs group
		sceneSpatialTransition.onChange((value) => {
			if (threeProperty === 'none') return;
			fadeGroupRef.current?.[threeProperty]?.set?.(...value);
			// const originGroup = threeScene.getObjectByName('origin-group');
			// originGroup?.[threeProperty]?.set?.(...value);
		});
		transitionManager[sceneId].startAll();

		// Hide the scene group immediately when it should fade out and the original duration is undefined/0 
		// and the duration hasn't been extended beyond the 100ms required so that pointer events don't affect 
		// both scenes (no child with longer transition).
		const hideContentImmediately = effect === ITransitionEffectTypes.fadeOut && (!originalDuration && duration === 100);
		if (fadeGroup) setTimeout(() => fadeGroup.visible = !hideContentImmediately, 0);

	}, [scene, sceneId, contentIds, enterOrExit, sceneDispatch, componentsById, transitionCategory, scene.transitions, sceneSpatialTransition]);

	return (
		<FadeGroup sceneId={sceneId} name={sceneId} ref={fadeGroupRef} scale={1} position={memoZeroTuple3 as any} transitionCategory={transitionCategory} transition={sceneOpacityTransition} sceneLevel visible={visible}>
			<ArComponents sceneId={sceneId} />
		</FadeGroup>
	);
};

Scene.displayName = 'Scene';
export default memo(Scene);
